import { t } from 'i18next';
import { Trans } from 'react-i18next';

import {
    Anchor,
    Body1,
    Body2,
    Box,
    Checkbox,
    Currency,
    FilledCard,
    Flex,
    Stack,
} from '@myphx-engineering/phx-design-system';
import type { SemanticColorVariants } from '@myphx-engineering/phx-design-system/framework';

import type { AuthorizedMedicationInfoFragment } from '../../../graphql/generated/graphql';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

type GenericSavingsTranslationProps = {
    authorizedMedicationSavings: number;
    footerColor: SemanticColorVariants;
    showAuthorizedOffersChecked: boolean;
    toggleGenericModal(): void;
};

const GenericSavingsTranslation = ({
    authorizedMedicationSavings,
    footerColor,
    showAuthorizedOffersChecked,
    toggleGenericModal,
}: GenericSavingsTranslationProps) => {
    return showAuthorizedOffersChecked ? (
        <Trans
            i18nKey="drx.product.details.savingWithGeneric"
            components={{
                1: <Currency amount={authorizedMedicationSavings} />,
                2: (
                    <Anchor
                        color={footerColor}
                        bold
                        highContrast
                        onClick={toggleGenericModal}
                    />
                ),
            }}
        />
    ) : (
        <Trans
            i18nKey="drx.product.details.saveWithGeneric"
            components={{
                1: <Currency amount={authorizedMedicationSavings} />,
                2: (
                    <Anchor
                        color={footerColor}
                        bold
                        highContrast
                        onClick={toggleGenericModal}
                    />
                ),
            }}
        />
    );
};

type GenericSwapCardFooterProps = {
    authorizedMedication: AuthorizedMedicationInfoFragment;
    authorizedMedicationSavings: number;
    footerColor: SemanticColorVariants;
    showAuthorizedOffersChecked: boolean;
    toggleCheckbox(): void;
    toggleGenericModal(): void;
};

export const GenericSwapCardFooter = ({
    authorizedMedication,
    authorizedMedicationSavings,
    footerColor,
    showAuthorizedOffersChecked,
    toggleCheckbox,
    toggleGenericModal,
}: GenericSwapCardFooterProps) => {
    return (
        <FilledCard color={footerColor} sharp>
            <Flex>
                <Box flex={1}>
                    <Stack gap="xxxs" color={footerColor}>
                        <Body1 bold inline color={footerColor} highContrast>
                            <GenericSavingsTranslation
                                authorizedMedicationSavings={
                                    authorizedMedicationSavings
                                }
                                footerColor={footerColor}
                                showAuthorizedOffersChecked={
                                    showAuthorizedOffersChecked
                                }
                                toggleGenericModal={toggleGenericModal}
                            />
                        </Body1>

                        <ConditionalComponent
                            condition={showAuthorizedOffersChecked}
                        >
                            <Body2 color={footerColor} highContrast>
                                {t(
                                    'drx.product.details.savingWithGenericPrices'
                                )}
                                {authorizedMedication.name}
                            </Body2>
                        </ConditionalComponent>
                    </Stack>
                </Box>

                <Checkbox
                    checked={showAuthorizedOffersChecked}
                    color="saving"
                    highContrast
                    onChange={toggleCheckbox}
                />
            </Flex>
        </FilledCard>
    );
};
